import * as React from 'react';
import KnowledgeService from '@lifeomic/phc-web-toolkit/src/services/KnowledgeService';
import { Box } from '@lifeomic/chroma-react/components/Box';
import { config as responseTypeConfig } from './filterOptions/responseType';
import { config as approvalStatusConfig } from './filterOptions/approvalStatus';
import { config as efficacyEvidenceConfig } from './filterOptions/efficacyEvidence';
import { defineMessages, useIntl } from 'react-intl';
import { InfiniteScroll } from '@lifeomic/chroma-react/components/InfiniteScroll';
import { JaxTypeahead } from '../JaxCancerEffect/filterOptions/JaxTypeahead';
import { KnowledgeFilterLayout } from '../KnowledgeFilterLayout';
import { makeStyles } from '@lifeomic/chroma-react/styles';
import { reduceFilters } from '../../FilterResultsManager/utils';
import { TableModule } from '@lifeomic/chroma-react/components/TableModule';
import { Text } from '@lifeomic/chroma-react/components/Text';
import { useEffectOnce, useUpdateEffect } from 'react-use';
import { useNotify } from '@lifeomic/phc-web-toolkit/src/hooks';
import { usePagination } from '@lifeomic/phc-web-toolkit/src/services/util/usePagination';

const knowledgeService = new KnowledgeService();

const useStyles = makeStyles(theme => ({
  resultsContentBox: {
    boxShadow: theme.boxShadows.table,
    maxHeight: '100%',
    maxWidth: '100%',
    overflowX: 'auto',
  },
  molecularProfileCell: {
    width: theme.pxToRem(240),
    whiteSpace: 'normal',
  },
  efficacyEvidenceCell: {
    width: theme.pxToRem(400),
    whiteSpace: 'normal',
  },
  caption: {
    marginTop: theme.spacing(1),
  },
}));

const messages = defineMessages({
  copyright: {
    id: 'omicsExplorer.variantDetails.jaxGeneDrugEvidenceContent.copyright',
    defaultMessage:
      '©Somatic gene variant annotations and related content have been powered by The Jackson Laboratory Clinical Knowledgebase (JAX-CKB™)',
  },
  molecularProfile: {
    id: 'omicsExplorer.variantDetails.jaxGeneDrugEvidenceContent.molecularProfile',
    defaultMessage: 'Molecular Profile',
  },
  indication: {
    id: 'omicsExplorer.variantDetails.jaxGeneDrugEvidenceContent.indication',
    defaultMessage: 'Indication',
  },
  responseType: {
    id: 'omicsExplorer.variantDetails.jaxGeneDrugEvidenceContent.responseType',
    defaultMessage: 'Response Type',
  },
  therapyName: {
    id: 'omicsExplorer.variantDetails.jaxGeneDrugEvidenceContent.therapyName',
    defaultMessage: 'Therapy Name',
  },
  approvalStatus: {
    id: 'omicsExplorer.variantDetails.jaxGeneDrugEvidenceContent.approvalStatus',
    defaultMessage: 'Approval Status',
  },
  efficacyEvidence: {
    id: 'omicsExplorer.variantDetails.jaxGeneDrugEvidenceContent.efficacyEvidence',
    defaultMessage: 'Efficacy Evidence',
  },
  error: {
    id: 'omicsExplorer.variantDetails.jaxGeneDrugEvidenceContent.error',
    defaultMessage: 'There was an error with the request.',
  },
  accountError: {
    id: 'omicsExplorer.variantDetails.jaxGeneDrugEvidenceContent.accountError',
    defaultMessage:
      'Account not authorized to view this content. Contact LifeOmic for access.',
  },
});

const formatBoldText = (label: string, value: string) => {
  if (!value) {
    return label;
  }
  return (
    <span>
      {/* @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks. */}
      {label.split(value).reduce((prev, current, i) => {
        if (!i) {
          return [current];
        }
        // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
        return prev.concat(<b key={value + current}>{value}</b>, current);
      }, [])}
    </span>
  );
};

const getTableConfig = ({
  // @ts-expect-error This directive was added as part of a bulk-change to enable noImplicitAny.
  classes, // @ts-expect-error This directive was added as part of a bulk-change to enable noImplicitAny.
  formatMessage, // @ts-expect-error This directive was added as part of a bulk-change to enable noImplicitAny.
  gene,
}) => [
  {
    header: { label: formatMessage(messages.approvalStatus) },
    cell: {
      content: (data: any) => data.approval_status,
    },
  },
  {
    header: { label: formatMessage(messages.molecularProfile) },
    cell: {
      content: (data: any) => {
        const profileName = data.molecular_profile_name;
        const formattedName = profileName.includes(gene)
          ? formatBoldText(profileName, gene)
          : profileName;

        return (
          <div className={classes.molecularProfileCell}>{formattedName}</div>
        );
      },
    },
  },
  {
    header: { label: formatMessage(messages.indication) },
    cell: {
      content: (data: any) => data.name,
    },
  },
  {
    header: { label: formatMessage(messages.responseType) },
    cell: {
      content: (data: any) => data.response_type,
    },
  },
  {
    header: { label: formatMessage(messages.therapyName) },
    cell: {
      content: (data: any) => data.therapy_name,
    },
  },
  {
    header: { label: formatMessage(messages.efficacyEvidence) },
    cell: {
      content: (data: any) => (
        <div className={classes.efficacyEvidenceCell}>
          {data.efficacy_evidence}
        </div>
      ),
    },
  },
];

const getFilterOptions = (gene: string) => [
  { ...approvalStatusConfig },
  {
    content: JaxTypeahead,
    id: 'jaxCancerEffectContent.filters.molecularProfile',
    injectedProps: {
      gene,
      id: 'jaxCancerEffectContent.filters.molecularProfile',
      searchKey: 'molecular_profile_name',
      service: knowledgeService.getEvidence,
    },
    title: {
      id: 'jaxCancerEffectContent.filters.molecularProfile',
      defaultMessage: 'Molecular Profile',
    },
  },
  {
    content: JaxTypeahead,
    id: 'jaxCancerEffectContent.filters.indication',
    injectedProps: {
      gene,
      id: 'jaxCancerEffectContent.filters.indication',
      searchKey: 'name',
      service: knowledgeService.getEvidence,
    },
    title: {
      id: 'jaxCancerEffectContent.filters.indication',
      defaultMessage: 'Indication',
    },
  },
  { ...responseTypeConfig },
  {
    content: JaxTypeahead,
    id: 'jaxCancerEffectContent.filters.therapyName',
    injectedProps: {
      gene,
      id: 'jaxCancerEffectContent.filters.therapyName',
      searchKey: 'therapy_name',
      service: knowledgeService.getEvidence,
    },
    title: {
      id: 'jaxCancerEffectContent.filters.therapyName',
      defaultMessage: 'Therapy Name',
    },
  },
  { ...efficacyEvidenceConfig },
];

export const JaxGeneDrugEvidenceContent = ({ gene }: { gene: string }) => {
  const [isLocalStateLoading, setIsLocalStateLoading] = React.useState(false);
  const [selectedFilterOptions, setSelectedFilterOptions] = React.useState([]);
  const filterOptions = getFilterOptions(gene);

  const {
    error,
    hasMoreToLoad,
    isLoading,
    loadMore,
    makeFetchRequest,
    results,
  } = usePagination(knowledgeService.getEvidence);

  const classes = useStyles({});
  const { formatMessage } = useIntl();
  const [addNote] = useNotify();

  useEffectOnce(() => {
    if (!gene) {
      return;
    }
    makeFetchRequest({ gene, pageSize: 50 });
  });

  useUpdateEffect(() => {
    if (!gene) {
      return;
    }

    const filters = reduceFilters(selectedFilterOptions);
    setIsLocalStateLoading(true);

    const debouncedFetch = setTimeout(() => {
      makeFetchRequest({ filters, gene, pageSize: 50 });
      setIsLocalStateLoading(false);
    }, 500);

    return () => {
      clearTimeout(debouncedFetch);
      setIsLocalStateLoading(false);
    };
  }, [selectedFilterOptions]);

  React.useEffect(() => {
    const errorStatus = error?.response?.status;

    if (errorStatus) {
      if (errorStatus === 402) {
        addNote(formatMessage(messages.accountError), 'error');
        return;
      }
      addNote(formatMessage(messages.error), 'error');
    }
  }, [addNote, error, formatMessage]);

  return (
    <Box align="center" direction="column">
      <KnowledgeFilterLayout
        filterOptions={filterOptions}
        selectedFilterOptions={selectedFilterOptions}
        setSelectedFilterOptions={setSelectedFilterOptions}
      >
        <div className={classes.resultsContentBox}>
          <InfiniteScroll
            hasNextPage={hasMoreToLoad}
            loading={isLocalStateLoading || isLoading}
            loadMore={loadMore}
            scrollOffset={1000}
            scrollContainer="parent"
          >
            <TableModule
              config={getTableConfig({ classes, formatMessage, gene })}
              data={results}
              data-testid="jax-gene-drug-evidence-table"
              isLoading={isLocalStateLoading || isLoading}
            />
          </InfiniteScroll>
        </div>
      </KnowledgeFilterLayout>
      <Text className={classes.caption} size="caption">
        {formatMessage(messages.copyright)}
      </Text>
    </Box>
  );
};
